import React from 'react';
import {
    Grid, IconButton, TextField as MuiTextField, Autocomplete
} from '@mui/material';
import { CorporateFare as SniIcon, Remove as RemoveIcon } from '@mui/icons-material';
import { TextField } from '../../CommonComponents/Form';
import { SectionDivider } from '../../CommonComponents';
import toast from '../../../utils/toast';
import { SniIndustryType } from '../../../types';

interface Sni { sniCode: string; name: string; }

interface SNIBlockProps {
    entity: any;
    onChange: (e: React.ChangeEvent<any>) => void;
    settables: SniIndustryType[];
    isFetching: boolean;
    snis: Sni[];
    setterSnis: (option: any) => void;
}

const SNIBlock = ({
    entity,
    onChange,
    settables,
    isFetching,
    snis,
    setterSnis
}: SNIBlockProps) => {
    const [selectedOption, setSelectedOption] = React.useState<SniIndustryType | null>(null);
    const [searchQuery, setSearchQuery] = React.useState('');

    const addSniIndustryFromList = React.useCallback((selected: SniIndustryType | null) => {
        if (!selected) return;
        const sniCode = String(selected.sniCode || '').trim();
        const name = String(selected.name || '').trim();
        const value = `${sniCode} ${name}`.trim();

        if (!sniCode || !name || !value) return;

        if ((entity.sniIndustries || []).includes(value)) {
            toast('Denna SNI-bransch finns redan i listan');
            return;
        }

        const newSni = {
            sniCode,
            name
        };

        setterSnis((prev: SniIndustryType[]) => [...prev, newSni]);
        onChange({
            ...entity,
            sniIndustries: [...(entity.sniIndustries || []), value]
        });
    }, [entity, onChange, setterSnis]);

    const removeSniIndustry = React.useCallback((sni: Sni) => {
        const value = `${sni.sniCode} ${sni.name}`.trim();
        setterSnis((prev: SniIndustryType[]) => prev
            .filter((s) => !(s.sniCode === sni.sniCode && s.name === sni.name)));
        onChange({
            ...entity,
            sniIndustries: (entity.sniIndustries || []).filter((ind: string) => ind !== value)
        });
    }, [entity, onChange, setterSnis]);

    return (
        <Grid item lg={12} xl={6}>
            <SectionDivider
                label={`SNI-brancher (${entity.sniIndustries?.length || 0})`}
                icon={<SniIcon fontSize="small" />}
                marginTop={2}
                marginBottom={0}
            />
            <Grid container spacing={2} sx={{ mb: 1 }}>
                <Grid item xs={12}>
                    <Autocomplete<SniIndustryType>
                        value={selectedOption}
                        inputValue={searchQuery}
                        options={settables || []}
                        loading={isFetching}
                        onChange={(_, newValue) => {
                            addSniIndustryFromList(newValue);
                            setSelectedOption(null);
                            setSearchQuery('');
                        }}
                        onInputChange={(_, newInputValue) => {
                            setSearchQuery(newInputValue);
                        }}
                        getOptionLabel={(option) => `${option.sniCode} ${option.name}`.trim()}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderInput={(params) => (
                            <MuiTextField
                                {...params}
                                label="Sök SNI-bransch"
                                placeholder="Skriv kod eller namn"
                                size="small"
                            />
                        )}
                    />
                </Grid>
            </Grid>
            <Grid container spacing={2}>
                {snis.map((sni) => (
                    <Grid item xs={12} key={sni.sniCode}>
                        <Grid container spacing={1} alignItems="center">
                            <Grid item xs>
                                <TextField
                                    name={`${sni.sniCode}`}
                                    label=""
                                    value={`${sni.sniCode} ${sni.name}`.trim()}
                                    disabled
                                    onChange={() => { }}
                                />
                            </Grid>
                            <Grid item>
                                <IconButton
                                    onClick={() => removeSniIndustry(sni)}
                                    color="error"
                                    size="small"
                                >
                                    <RemoveIcon />
                                </IconButton>
                            </Grid>
                        </Grid>
                    </Grid>
                ))}
            </Grid>
        </Grid>
    );
};

export default React.memo(SNIBlock);
